/**
 * Featured Works mix — redraws the strip on every visit.
 *
 * Markup (sections/featured-collection.liquid, mix mode):
 *   <featured-mix>
 *     <div data-featured-group hidden>          one per collection
 *       <template data-featured-option>…card…</template>   newest available pieces
 *     </div>
 *     <product-slider> … <div data-slide data-featured-slot data-group="0" data-option="0">default card</div> …
 *
 * The slots are server-rendered round-robin (piece 1 of each collection, then
 * piece 2 of each, …) so the strip works without JS. Here the collections are
 * shuffled and the slots are filled from them in turn, each with a random
 * piece not shown yet: with three collections and three slots that is one
 * piece from each; with a single collection (Top Picks) it is three different
 * pieces from it. A slot whose draw equals its default is left alone.
 *
 * Only the slot's contents are swapped — the slot elements stay, so
 * <product-slider>'s dots and observer keep working whichever script upgrades
 * first. Cloned <product-card>s upgrade on insertion, and the inquiry modal
 * listens on document, so every button on a swapped card works.
 */
import { Component } from "@theme/component";

/** Fisher–Yates, returns a new array. */
function shuffled(items) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

class FeaturedMix extends Component {
  setup() {
    const groups = [...this.$$("[data-featured-group]")]
      .map((group, index) => ({
        index,
        options: [...group.querySelectorAll("template[data-featured-option]")],
      }))
      .filter((group) => group.options.length);
    const slots = [...this.$$("[data-featured-slot]")];
    if (!groups.length || !slots.length) return;

    const order = shuffled(groups);
    const shown = new Map(order.map((group) => [group, new Set()]));
    slots.forEach((slot, i) => {
      // Start at this slot's collection; move on if it has nothing left.
      for (let step = 0; step < order.length; step++) {
        const group = order[(i + step) % order.length];
        const used = shown.get(group);
        const free = group.options.map((_, k) => k).filter((k) => !used.has(k));
        if (!free.length) continue;
        const option = free[Math.floor(Math.random() * free.length)];
        used.add(option);
        const isDefault =
          slot.dataset.group === String(group.index) &&
          slot.dataset.option === String(option);
        if (!isDefault) {
          slot.replaceChildren(group.options[option].content.cloneNode(true));
          // Keep the label true to what the slot now shows, so a second
          // setup (element re-attached) never mistakes it for the default.
          slot.dataset.group = String(group.index);
          slot.dataset.option = String(option);
        }
        return;
      }
    });
  }
}

customElements.define("featured-mix", FeaturedMix);

export { FeaturedMix };
